import { Core } from "@faire/design-tokens";
import { trackCoreCollectionsAddDisplayClick } from "@faire/web-api/events/coreCollections/click/addDisplay";
import { IRetailerProductBasedCollectionResponse } from "@faire/web-api/indigofair/data/IRetailerProductBasedCollectionResponse";
import { optimizeImageUrl } from "@faire/web/common/images/optimizeImages";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { Button } from "@faire/web/slate/Button";
import { Column, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { observer } from "mobx-react";
import { useCallback } from "react";
import * as React from "react";
import styled from "styled-components";

import {
  ICartItemForUpdate,
  UpdateCartHandler,
} from "@faire/retailer/stores/domain/CartStore/consts";
import { AddToCartVariants } from "@faire/retailer/stores/domain/CartStore/triggerAddToCartEvent";
import { useShowCollectionDisplayPromoTrackingBanner } from "@faire/retailer/views/brand/BrandCart/hooks/useShowCollectionDisplayPromoTrackingBanner";
import { AddToCartAnimation } from "@faire/retailer/views/product-based-collection/__internal__/DisplayCollectionPromo/AddToBagAnimation";

import IRetailerProductBasedCollection = IRetailerProductBasedCollectionResponse.IRetailerProductBasedCollection;

const DisplayStickyActionBannerBase: React.FC<{
  collection: IRetailerProductBasedCollection;
  updateItems: UpdateCartHandler;
}> = ({ collection, updateItems }) => {
  const shouldShowDisplayTracker =
    useShowCollectionDisplayPromoTrackingBanner();
  const isSSR = useIsSSR();

  const [showToastsBanner, setShowToastBanner] = React.useState<boolean>(false);

  const onAnimationComplete = useCallback(() => {
    setShowToastBanner(false);
  }, []);

  const addDisplayProductToCart = async () => {
    const product =
      collection.active_display_promo?.free_display_product?.tile?.product;
    if (product) {
      trackCoreCollectionsAddDisplayClick(
        collection.token ?? "",
        product.token ?? ""
      );
      await updateItems(
        [
          ICartItemForUpdate.build({
            token: product.token,
            product_token: product.token,
            brand_token: collection.brand_token,
            product_option_token:
              collection.active_display_promo?.free_display_product?.tile
                ?.selected_product_option_token,
            quantity: 1,
          }),
        ],
        {
          upltParams: {
            event: "add-to-cart",
            startTime: Date.now(),
            variant: AddToCartVariants.FREE_DISPLAY,
          },
        }
      ).then(() => {
        setShowToastBanner(true);
      });
    }
  };

  if (shouldShowDisplayTracker) {
    return (
      <StickyActionBanner>
        <ProductImage
          src={optimizeImageUrl(
            {
              url: collection.active_display_promo?.free_display_product
                ?.metadata?.image?.url,
            },
            isSSR,
            {
              width: DISPLAY_PRODUCT_IMAGE_DIMENSION,
              height: DISPLAY_PRODUCT_IMAGE_DIMENSION,
            }
          )}
        />
        <Spacer size={12} />
        <Column justify="center">
          <Typography variant="paragraphSansMedium">
            <StrictLocalMsg
              defaultMessage="You unlocked a free display!"
              description={{
                text: "Text informing the user that they unlocked a free merchandising display promotion.",
              }}
            />
          </Typography>
          <Spacer size="1x" />
          <StyledAddToCartButton onClick={addDisplayProductToCart}>
            <StrictLocalMsg
              defaultMessage="Add to bag"
              description={{
                text: "CTA to add this item to bag",
              }}
            />
          </StyledAddToCartButton>
        </Column>
      </StickyActionBanner>
    );
  }

  if (showToastsBanner) {
    return (
      <AddToCartAnimation
        onAnimationComplete={onAnimationComplete}
        displayProductImageUrl={
          collection.active_display_promo?.free_display_product?.metadata?.image
            ?.url
        }
      />
    );
  }

  return null;
};

export const DisplayStickyActionBanner = observer(
  DisplayStickyActionBannerBase
);

export const DISPLAY_PRODUCT_IMAGE_DIMENSION: number = 72;

const ProductImage = styled.img`
  width: ${DISPLAY_PRODUCT_IMAGE_DIMENSION}px;
  height: ${DISPLAY_PRODUCT_IMAGE_DIMENSION}px;
`;
const StickyActionBanner = styled(Row)`
  background-color: ${Core.surface.inverse};
  position: sticky;
  z-index: 5;
  bottom: ${getSpacing(12)};
  right: ${getSpacing(12)};
  width: 360px;
  max-width: calc(100% - ${getSpacing("3x")});
  padding: ${getSpacing(12)};
  border: 1px solid ${Core.border.muted};
  margin-left: auto;
`;

const StyledAddToCartButton = styled(Button)`
  height: 34px;
  width: fit-content;
`;
